import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { copyToClipboard, fixDate, getMapMarker } from "../../helpers/helperFunctions";
import { MarkerF, InfoBox } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { icons } from "../../helpers/constants";
import Button from "../Buttons/Button";
import NewButton from "../Buttons/NewButton";

const toTs = (iso) => (iso ? Date.parse(iso) : -Infinity);

const DeviceMarker = ({ device, hideOffline, clusterer, clicked, isOpen, showName }) => {
	const { t } = useTranslation();

	const [address, setAddress] = useState(t("map.calculate_address"));
	const [showNameInner, setShowNameInner] = useState(false);
	const [copy, setCopy] = useState(t("map.copy_position"));

	const latVal = device?.positioninfo?.latitude;
	const lngVal = device?.positioninfo?.longitude;
	const positionInfo = device?.positioninfo;

	// allow 0, block only null/undefined
	const latLng = latVal == null || lngVal == null ? null : { lat: latVal, lng: lngVal };

	const [position, setPosition] = useState(latLng);

	// rAF + timestamp guards
	const rafRef = useRef(null);
	const lastAppliedTsRef = useRef(toTs(positionInfo?.datetime));

	// cancel rAF on unmount
	useEffect(() => {
		return () => {
			if (rafRef.current != null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, []);

	// keep address label in sync with language changes (optional but nice)
	useEffect(() => {
		setAddress((prev) => (prev === t("map.calculate_address") ? t("map.calculate_address") : prev));
		setCopy((prev) => (prev === t("map.copy_position") ? t("map.copy_position") : prev));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t]);

	const animateMarker = useCallback((start, end) => {
		if (!start) {
			setPosition(end);
			return;
		}

		let startTime = null;
		const duration = 1000;

		const step = (ts) => {
			if (startTime == null) startTime = ts;
			const progress = Math.min(1, (ts - startTime) / duration);

			if (progress < 1) {
				setPosition({
					lat: start.lat + (end.lat - start.lat) * progress,
					lng: start.lng + (end.lng - start.lng) * progress,
				});
				rafRef.current = requestAnimationFrame(step);
			} else {
				rafRef.current = null;
				setPosition(end);
			}
		};

		rafRef.current = requestAnimationFrame(step);
	}, []);

useEffect(() => {
	if (latVal == null || lngVal == null) return;

	const next = { lat: latVal, lng: lngVal };

	const newTs = toTs(positionInfo?.datetime);
	if (newTs !== -Infinity) {
		if (newTs < lastAppliedTsRef.current) return;
		lastAppliedTsRef.current = newTs;
	}

	// avoid animating first set or same point
	if (!position || (position.lat === next.lat && position.lng === next.lng)) {
		setPosition(next);
		return;
	}

	if (rafRef.current != null) {
		cancelAnimationFrame(rafRef.current);
		rafRef.current = null;
	}

	animateMarker(position, next);
	// eslint-disable-next-line react-hooks/exhaustive-deps
}, [latVal, lngVal, positionInfo?.datetime, device?.id]);

	const calculateAddress = useCallback(
		(lat, lng) => {
			if (address !== t("map.calculate_address")) return;

			const geocoder = new window.google.maps.Geocoder();
			const latLngObj = new window.google.maps.LatLng(lat, lng);

			geocoder.geocode({ location: latLngObj }, (results, status) => {
				if (status === "OK" && results?.[0]) setAddress(results[0].formatted_address);
			});
		},
		[address, t]
	);

	// IMPORTANT: clear timeout on unmount so you don't set state after unmount
	const copyTimeoutRef = useRef(null);

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const copyPosition = useCallback(
		(lat, lng) => {
			const data = `https://www.google.com/maps/place/${lat},${lng}`;

			copyToClipboard(data)
				.then(() => setCopy(t("map.copy_successful")))
				.catch(() => setCopy(t("map.copy_failed")));

			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
			copyTimeoutRef.current = setTimeout(() => {
				setCopy(t("map.copy_position"));
			}, 2000);
		},
		[t]
	);

	// ---- The big stability fix: memoize google objects/options ----
	const googleReady = !!window?.google?.maps;
	const isOnline = (device?.status || "").toLowerCase() === "online";

	// const markerIcon = () => {
	// 	return{
	// 		url: getMapMarker(device.mapicon, isOnline),
	// 		scaledSize: new window.google.maps.Size(45, 45),
	// 		anchor: new window.google.maps.Point(22.5, 40),
	// 	}
	// }

	const markerIcon = useMemo(() => {
		if (!googleReady) return undefined;

		return {
			url: getMapMarker(device.mapicon, isOnline),
			scaledSize: new window.google.maps.Size(45, 45),
			anchor: new window.google.maps.Point(22.5, 40),
		};
	}, [googleReady, device.mapicon, isOnline]);

	const deviceInfoBoxOptions = useMemo(() => {
		if (!googleReady) return undefined;

		return {
			disableAutoPan: true,
			closeBoxURL: "",
			boxStyle: {
				width: "300", // <-- use px
				boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0.10), 0px 3px 7px 0px rgba(0, 0, 0, 0.10), 0px 13px 13px 0px rgba(0, 0, 0, 0.09), 0px 28px 17px 0px rgba(0, 0, 0, 0.05), 0px 51px 20px 0px rgba(0, 0, 0, 0.01), 0px 79px 22px 0px rgba(0, 0, 0, 0.00)",
				borderRadius: "12px",
			},
			pixelOffset: new window.google.maps.Size(-175, 2),
		};
	}, [googleReady]);

	const nameInfoBoxOptions = useMemo(() => {
		if (!googleReady) return undefined;

		return {
			disableAutoPan: true,
			closeBoxURL: "",
			boxStyle: {
				width: "100px",
				boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.10), 0px 3px 3px 0px rgba(0, 0, 0, 0.09), 0px 8px 5px 0px rgba(0, 0, 0, 0.05), 0px 14px 5px 0px rgba(0, 0, 0, 0.01), 0px 21px 6px 0px rgba(0, 0, 0, 0.00)",
				borderRadius: "6px",
				height: "auto",
				overflow: "hidden",
			},
			pixelOffset: new window.google.maps.Size(-50, 2),
		};
	}, [googleReady]);

	// ---- Stop propagation helper (fixes button closing/jumping) ----
	const stop = useCallback((e) => {
		e.preventDefault?.();
		e.stopPropagation?.();
	}, []);

	if (!latLng || (hideOffline && !isOnline)) return null;

	return (
		<MarkerF
			clusterer={clusterer}
			position={position}
			icon={markerIcon}
			onMouseOut={() => setShowNameInner(false)}
			onMouseOver={() => !isOpen && setShowNameInner(true)}
			onClick={() => {
				setShowNameInner(false);
				clicked();
			}}
		>
			{isOpen && (
				<InfoBox position={position} options={deviceInfoBoxOptions}>
					<div className="d-flex flex-column device-info-box" onMouseDown={stop} onPointerDown={stop} onClick={stop}>
						<div className="d-flex flex-row justify-content-between align-items-center border-settings p-3 bg-white">
							<div className="d-flex flex-row align-items-center w-75">
								<img className="size_25 me-2" src={isOnline ? icons.signal_green : icons.signal_red} alt="" />
								<h3 className="me-3 text-truncate primary">{device.name}</h3>
							</div>

							<img
								className="cursor-pointer"
								src={icons.close_gray}
								alt="..."
								onMouseDown={stop}
								onClick={(e) => {
									stop(e);
									clicked();
								}}
							/>
						</div>

						<div className="d-flex flex-column py-3 px-4 bg-white">
							<div className="d-flex flex-row">
								<div className="d-flex flex-row align-items-center">
									<img className="size_25 me-2 grey-blue-icon" src={icons.marker} alt="" />
									<div className="d-flex flex-column align-items-start">
										<p className="bold">{t("map.position")}</p>
										<p>
											{position.lat.toFixed(5)}, {position.lng.toFixed(5)}
										</p>
										<p
											className="cursor-pointer"
											onMouseDown={stop}
											onClick={(e) => {
												stop(e);
												calculateAddress(position.lat, position.lng);
											}}
										>
											{address}
										</p>
									</div>
								</div>
							</div>

							<div className="d-flex flex-row mt-3 justify-content-between">
								<div className="d-flex flex-column align-items-start">
									<div className="d-flex flex-row align-items-center">
										<img className="size_25 me-2 grey-blue-icon" src={icons.clock} alt="" />
										<div className="d-flex flex-column align-items-start">
											<p className="bold">{t("map.connection_time")}</p>
											<p>{fixDate(device?.lastupdate, false, true)}</p>
										</div>
									</div>

									<div className="d-flex flex-row align-items-center mt-3">
										<img className="size_25 me-2 grey-blue-icon" src={icons.tachometer} alt="" />
										<div className="d-flex flex-column align-items-start">
											<p className="bold">{t("map.speed")}</p>
											<p>
												{positionInfo?.currentspeed?.info} {positionInfo?.currentspeed?.unit}
											</p>
										</div>
									</div>
								</div>

								<div className="d-flex flex-column">
									<div className="d-flex flex-row align-items-center">
										<img className="size_25 me-2 grey-blue-icon" src={icons.pin} alt="" />
										<div className="d-flex flex-column align-items-start">
											<p className="bold">{t("map.position_time")}</p>
											<p>{fixDate(positionInfo?.datetime, false, true)}</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						<NewButton
							className="bg-grey-blue bold white"
							onMouseDown={stop}
							onClick={(e) => {
								stop(e);
								copyPosition(position.lat, position.lng);
							}}
						>
							{copy}
						</NewButton>
					</div>
				</InfoBox>
			)}

			{((showName && !isOpen) || (showNameInner && !isOpen)) && (
				<InfoBox position={position} options={nameInfoBoxOptions}>
					<div onMouseDown={stop} onPointerDown={stop} onClick={stop} className="px-2 d-flex bg-white py-1 p bold primary justify-content-center">
						<p className="text-truncate overflow-hidden m-0 p-0">{device.name}</p>
					</div>
				</InfoBox>
			)}
		</MarkerF>
	);
};

export default DeviceMarker;
